import { ListRenderItem, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLinkTo } from '@react-navigation/native';

import { FlashList, Text, spacing } from '@pletly/ui';
import { useUser } from '%hooks';
import { useRefreshControl } from '%components/ListDefaultLayout/useRefreshControl';
import { useYourCommunities } from './components/useYourCommunities';
import { Group } from '../components/GroupsList/Group';
import { YourCommunityGroupsQuery } from './components/__generated__/yourGroups.generated';

export const YourCommunities = () => {
	const { t } = useTranslation();
	const navigate = useLinkTo();
	const user = useUser();
	const { loading, data, refetch, acceptJoinInvitePress, rejectJoinInvitePress } =
		useYourCommunities();
	const { refreshControl } = useRefreshControl(refetch);

	if (loading) {
		return null;
	}

	if (!data) {
		return null;
	}

	const sectionsToDisplay: (string | Groups)[] = [
		[
			t('community.invitations'),
			data.currentUser.communityInvites.map((invite) => ({
				...invite.communityGroup,
				id: invite.communityGroup.id,
				inviteId: invite.id,
			})),
		],

		[t('community.community-you-belong-to'), data.currentUser.memberOfCommunityGroups],

		[t('community.community-you-manage'), data.currentUser.adminOfCommunityGroups],
	]
		.filter(([, groups]) => groups.length > 0)
		.flat();

	const renderItem: ListRenderItem<string | Groups> | null | undefined = ({ item, index }) => {
		if (typeof item === 'string') {
			return <Name name={item} />;
		}

		const groups = item;

		return (
			<View style={styles.groups}>
				{groups.map((group) => {
					const sectionType = sectionsToDisplay[index - 1];
					const isMemberAlready = group.members.map((member) => member.id).includes(user.id);
					const onPress = isMemberAlready
						? () => navigate(`/communities/${group.uuid}`)
						: undefined;

					return (
						<Group
							key={group.id}
							name={group.name}
							description={group.description}
							members={group.members}
							onPress={onPress}
							onJoinPress={
								sectionType === t('community.invitations') && !isMemberAlready
									? () =>
											acceptJoinInvitePress(
												(group as unknown as GroupWithInviteId).inviteId,
												group.uuid,
											)
									: undefined
							}
							onCancelPress={
								sectionType === t('community.invitations')
									? () => rejectJoinInvitePress((group as unknown as GroupWithInviteId).inviteId)
									: undefined
							}
						/>
					);
				})}
			</View>
		);
	};

	return (
		<View style={styles.lists}>
			<FlashList
				estimatedItemSize={232}
				data={sectionsToDisplay}
				refreshControl={refreshControl}
				renderItem={renderItem}
			/>
		</View>
	);
};

const Name = ({ name }: { name: string }) => (
	<Text category="h3" style={styles.header}>
		{name}
	</Text>
);

const styles = StyleSheet.create({
	lists: {
		flex: 1,
		gap: spacing(3),
	},
	groups: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: spacing(3),
	},
	header: {
		marginVertical: spacing(3),
	},
});

type Group = Omit<YourCommunityGroupsQuery['currentUser']['memberOfCommunityGroups'], '__typename'>;
type GroupWithInviteId = Group & {
	inviteId: string;
};
type Groups =
	| Group
	| GroupWithInviteId
	| Omit<YourCommunityGroupsQuery['currentUser']['adminOfCommunityGroups'], '__typename'>;
